import { useCallback, useEffect, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { DOWNLOAD_REASON_EMPTY_MESSAGE } from '@/utils/consts'
import { REASON_DETAIL_MAX_LENGTH, REASON_DETAIL_MIN_LENGTH } from './consts'

/** 컨덕터 `core.FormItem` 라벨 고정폭 — 산출물이 `labelWidth={88}` 로 내보낸다(DS 요소 기본값은 160 이라 명시 필수). */
const FI_LABEL_WIDTH = 88

interface Props {
  open: boolean
  /** 사유코드 옵션 — GET /api/v1/combos(DOWNLOAD_CD) 응답(호출부에서 주입, LOGISPRD-12385). */
  reasonOptions?: { value: string; label: string }[]
  onConfirm: (params: { reasonCode: string; reasonDetail: string }) => void
  onCancel: () => void
}

/**
 * 다운로드 사유 모달 — 컨덕터 `cmp.DownloadReasonModal` 산출물(#inboundDownloadModal) 구조 그대로:
 * `core.Modal('다운로드 사유'·520·취소) > core.Form(horizontal) > [FormItem 사유코드(required·88) Radio.Group · FormItem 상세사유(required·88) Input + Typography.Text(12·secondary)]`.
 * 1.0 RemarkPop 동치. 사유코드·상세사유 둘 다 필수라 확인은 둘이 채워질 때만 활성(데이터 검증은 dev 계약).
 *
 * pbo-core 통짜 `ExcelDownloadReasonModal` 은 쓰지 않는다 — `onConfirm()` 이 무인자라 사유값을 꺼낼 수 없고,
 * 파일 비밀번호가 필수인데 GR01 은 암호 zip 계약이 없다. 0.8.0 에서 그 통짜는 사라진다.
 */
export function ExcelDownloadReasonModal({ open, reasonOptions = [], onConfirm, onCancel }: Readonly<Props>) {
  const [reasonCode, setReasonCode] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')

  // 모달이 언마운트 없이 open 만 토글된다 — 비우지 않으면 이전 사유가 남는다.
  useEffect(() => {
    if (!open) return
    setReasonCode('')
    setReasonDetail('')
  }, [open])

  const isValid = Boolean(reasonCode) && reasonDetail.trim().length >= REASON_DETAIL_MIN_LENGTH

  const handleOk = useCallback(() => {
    if (!isValid) return
    onConfirm({ reasonCode, reasonDetail: reasonDetail.trim() })
  }, [isValid, reasonCode, reasonDetail, onConfirm])

  return (
    <core.Modal open={open} title="다운로드 사유" width={520} okText="확인" cancelText="취소" okButtonProps={{ disabled: !isValid }} onOk={handleOk} onCancel={onCancel}>
      <core.Form layout="horizontal">
        <core.FormItem label="사유코드" required labelWidth={FI_LABEL_WIDTH}>
          {reasonOptions.length === 0 ? (
            // 옵션이 비면 라디오가 하나도 안 그려지고 reasonCode 가 '' 로 남아 확인 버튼이 영영 비활성이다.
            // 콤보 조회 실패, DOWNLOAD_CD 미등록, 요청 코드 목록 누락 세 경로로 도달한다. 이유를 화면에 남긴다.
            // 문구는 EGR01·GI24 와 공유한다 — utils/consts.ts DOWNLOAD_REASON_EMPTY_MESSAGE(LOGISPRD-12615).
            <core.Typography.Text type="danger" style={{ fontSize: 12 }}>
              {DOWNLOAD_REASON_EMPTY_MESSAGE}
            </core.Typography.Text>
          ) : (
            <core.Radio.Group options={reasonOptions} value={reasonCode} onChange={(e) => setReasonCode(String(e.target.value))} />
          )}
        </core.FormItem>
        <core.FormItem label="상세사유" required labelWidth={FI_LABEL_WIDTH}>
          <core.Input value={reasonDetail} placeholder="사유를 입력하세요" maxLength={REASON_DETAIL_MAX_LENGTH} onChange={(e) => setReasonDetail(e.target.value)} style={{ width: '100%' }} />
          <core.Typography.Text style={{ fontSize: 12 }} type="secondary">{`${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해주세요.`}</core.Typography.Text>
        </core.FormItem>
      </core.Form>
    </core.Modal>
  )
}

export default ExcelDownloadReasonModal
